// AI generated
import type { Edge, Node } from '@xyflow/react'
import { useReactFlow } from '@xyflow/react'
import { useCallback } from 'react'

type ElementData = Record<string, unknown>

/**
 * Shallow, id-scoped merge into one element's `data`. Deliberately shallow: a
 * deep merge would change how nested values are overwritten. Elements whose id
 * does not match are returned by reference, so a memoized node or edge does not
 * re-render because a sibling changed.
 */
function mergeData<T extends Node | Edge>(
  elements: T[],
  id: string,
  newData: Partial<ElementData>,
): T[] {
  return elements.map((element) =>
    element.id === id
      ? ({ ...element, data: { ...element.data, ...newData } } as T)
      : element,
  )
}

export function useUpdateNodeData<Data extends ElementData>(id: string) {
  const { setNodes } = useReactFlow()

  return useCallback(
    (newData: Partial<Data>) => {
      setNodes((nodes) => mergeData(nodes, id, newData))
    },
    [setNodes, id],
  )
}

export function useUpdateEdgeData<Data extends ElementData>(id: string) {
  const { setEdges } = useReactFlow()

  return useCallback(
    (newData: Partial<Data>) => {
      setEdges((edges) => mergeData(edges, id, newData))
    },
    [setEdges, id],
  )
}
